import { useState, useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { UserButton } from '@clerk/clerk-react';
import {
  LayoutDashboard,
  Building2,
  FileText,
  Menu,
  X,
  ChevronLeft,
  Activity,
  ShieldCheck,
  Sun,
  Moon,
  Search,
  DollarSign,
  Plug,
  Package,
  Bell,
  Server,
  UserCog,
} from 'lucide-react';
import { DEV_AUTH_BYPASS } from '../devBypass';
import CommandPalette from '../components/common/CommandPalette';
import NotificationBell from '../components/common/NotificationBell';

interface NavItem {
  name: string;
  path: string;
  icon: React.ElementType;
}

const navItems: NavItem[] = [
  { name: 'Dashboard', path: '/dashboard', icon: LayoutDashboard },
  { name: 'Tenants', path: '/tenants', icon: Building2 },
  { name: 'Activity', path: '/activity', icon: Activity },
  { name: 'Revenue', path: '/billing', icon: DollarSign },
  { name: 'Integrations', path: '/integrations', icon: Plug },
  { name: 'Plans', path: '/plans', icon: Package },
  { name: 'Alerts', path: '/alerts', icon: Bell },
  { name: 'Users', path: '/users', icon: UserCog },
  { name: 'Audit Logs', path: '/audit-logs', icon: FileText },
  { name: 'System', path: '/system', icon: Server },
];

// Short descriptor shown under the page title in the header (keyed by nav name).
const PAGE_SUBTITLES: Record<string, string> = {
  Dashboard: 'Platform overview & key metrics',
  Tenants: 'Customer accounts & their applications',
  Activity: 'Live sessions & events across the platform',
  Revenue: 'MRR, churn & failed payments',
  Integrations: 'Connection health across all tenants',
  Plans: 'The plan catalog',
  Alerts: 'Platform alerts & notices',
  Users: 'Platform admins & role assignment',
  'Audit Logs': 'Security & activity trail',
  System: 'Health, services & operations',
};

// Titles for detail routes that aren't top-level nav items, so the header never
// falls back to "Dashboard" on a drill-down page.
const DETAIL_ROUTES: { prefix: string; title: string; subtitle: string }[] = [
  { prefix: '/applications/', title: 'Application', subtitle: 'Application detail & controls' },
  { prefix: '/agencies/', title: 'Agency', subtitle: 'Organization detail' },
  { prefix: '/tenants/', title: 'Tenant', subtitle: 'Account detail' },
];

interface AdminLayoutProps {
  children: React.ReactNode;
}

export default function AdminLayout({ children }: AdminLayoutProps) {
  const [sidebarOpen, setSidebarOpen] = useState(true);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const location = useLocation();

  const isActive = (path: string) => location.pathname === path;

  // Resolve the header title/subtitle. Exact nav match wins; then detail routes
  // (e.g. /applications/:id) that are no longer top-level tabs; then nav prefix
  // (e.g. /tenants/:id → Tenants); finally Dashboard.
  const exactNav = navItems.find((i) => location.pathname === i.path);
  const detail = DETAIL_ROUTES.find((d) => location.pathname.startsWith(d.prefix));
  const prefixNav = navItems.find((i) => location.pathname.startsWith(i.path + '/'));
  let pageTitle = 'Dashboard';
  let pageSubtitle = 'LumiTalk platform administration';
  if (exactNav) {
    pageTitle = exactNav.name;
    pageSubtitle = PAGE_SUBTITLES[exactNav.name] ?? pageSubtitle;
  } else if (detail) {
    pageTitle = detail.title;
    pageSubtitle = detail.subtitle;
  } else if (prefixNav) {
    pageTitle = prefixNav.name;
    pageSubtitle = PAGE_SUBTITLES[prefixNav.name] ?? pageSubtitle;
  }

  // Theme: initialised from the <html> class the inline boot script set (so no flash),
  // then kept in sync with localStorage.
  const [isDark, setIsDark] = useState(
    () => typeof document !== 'undefined' && document.documentElement.classList.contains('dark')
  );

  useEffect(() => {
    const root = document.documentElement;
    root.classList.toggle('dark', isDark);
    try {
      localStorage.setItem('lumiadmin-theme', isDark ? 'dark' : 'light');
    } catch {
      /* ignore storage failures */
    }
  }, [isDark]);

  // The Tenant Explorer is a multi-column drill-down; collapse the main nav to a
  // rail on entry so the columns get room (user can still re-open it manually).
  const isExplorer = location.pathname === '/tenants';
  useEffect(() => {
    if (isExplorer) setSidebarOpen(false);
  }, [isExplorer]);

  // Global search palette (Ctrl+K / Cmd+K)
  const [paletteOpen, setPaletteOpen] = useState(false);
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if ((e.ctrlKey || e.metaKey) && e.key.toLowerCase() === 'k') {
        e.preventDefault();
        setPaletteOpen((v) => !v);
      }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, []);

  return (
    <div className="min-h-screen bg-[#f1f3ec] dark:bg-[#0a0a0b]">
      {/* Mobile menu overlay */}
      {mobileMenuOpen && (
        <div
          className="fixed inset-0 bg-gray-900 bg-opacity-50 z-40 lg:hidden"
          onClick={() => setMobileMenuOpen(false)}
        />
      )}

      {/* Sidebar */}
      <aside
        className={`
          fixed top-0 left-0 z-50 h-full bg-white text-gray-700 border-r border-gray-200
          transition-all duration-300 ease-in-out
          ${sidebarOpen ? 'w-64' : 'w-20'}
          ${mobileMenuOpen ? 'translate-x-0' : '-translate-x-full lg:translate-x-0'}
        `}
      >
        {/* Sidebar Header */}
        <div className="h-16 flex items-center justify-between px-4 border-b border-gray-200">
          {sidebarOpen ? (
            <div className="flex items-center gap-2">
              {/* Light mode: full wordmark logo */}
              <img src={`${import.meta.env.BASE_URL}lumitalk_logo.png`} alt="LumiTalk" className="h-8 w-auto dark:hidden" />
              {/* Dark mode: icon + light wordmark (the wordmark's black text is invisible on dark) */}
              <div className="hidden dark:flex items-center gap-2">
                <img src={`${import.meta.env.BASE_URL}lumitalk_logo_sm.png`} alt="LumiTalk" className="w-8 h-8" />
                <span className="font-bold text-white text-lg leading-none">
                  Lumi<span className="text-primary-400">Talk</span>
                </span>
              </div>
              <span className="text-[9px] font-semibold uppercase tracking-wider text-primary-600 bg-primary-50 px-1.5 py-0.5 rounded">
                Admin
              </span>
            </div>
          ) : (
            <img src={`${import.meta.env.BASE_URL}lumitalk_logo_sm.png`} alt="LumiTalk" className="w-9 h-9 mx-auto" />
          )}

          {/* Toggle sidebar button - desktop */}
          <button
            onClick={() => setSidebarOpen(!sidebarOpen)}
            className={`hidden lg:flex p-1.5 rounded-lg text-gray-400 hover:bg-gray-100 hover:text-gray-700 ${!sidebarOpen && 'hidden'}`}
          >
            <ChevronLeft className={`w-5 h-5 transition-transform ${!sidebarOpen && 'rotate-180'}`} />
          </button>

          {/* Close button - mobile */}
          <button
            onClick={() => setMobileMenuOpen(false)}
            className="lg:hidden p-1.5 rounded-lg text-gray-400 hover:bg-gray-100 hover:text-gray-700"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* Navigation */}
        <nav className="p-3 space-y-1">
          {navItems.map((item) => {
            const Icon = item.icon;
            const active = isActive(item.path);

            return (
              <Link
                key={item.path}
                to={item.path}
                onClick={() => setMobileMenuOpen(false)}
                className={`
                  flex items-center space-x-3 px-3 py-2.5 rounded-lg transition-colors
                  ${active
                    ? 'bg-primary-400 text-[#1a1022] font-semibold shadow-sm'
                    : 'text-gray-600 hover:bg-black/5 hover:text-gray-900 dark:hover:bg-white/10 dark:hover:text-white'
                  }
                  ${!sidebarOpen && 'justify-center'}
                `}
                title={!sidebarOpen ? item.name : undefined}
              >
                <Icon className={`w-5 h-5 ${active ? 'text-[#1a1022]' : 'text-gray-400'}`} />
                {sidebarOpen && <span className="font-medium">{item.name}</span>}
              </Link>
            );
          })}
        </nav>
      </aside>

      {/* Main Content */}
      <div
        className={`
          transition-all duration-300 ease-in-out
          ${sidebarOpen ? 'lg:ml-64' : 'lg:ml-20'}
        `}
      >
        {/* Top Bar */}
        <header className="h-16 bg-white border-b border-gray-200 sticky top-0 z-30">
          <div className="h-full px-4 lg:px-6 flex items-center justify-between gap-4">
            {/* Left: mobile menu + page title */}
            <div className="flex items-center gap-3 min-w-0">
              <button
                onClick={() => setMobileMenuOpen(true)}
                className="lg:hidden p-2 -ml-2 rounded-lg hover:bg-gray-100"
                aria-label="Open menu"
              >
                <Menu className="w-6 h-6 text-gray-700" />
              </button>

              {/* Logo - mobile only */}
              <div className="lg:hidden flex items-center gap-2">
                <img src={`${import.meta.env.BASE_URL}lumitalk_logo_sm.png`} alt="LumiTalk" className="w-7 h-7" />
                <span className="font-bold text-gray-900">
                  Lumi<span className="text-primary-500">Talk</span>
                </span>
              </div>

              {/* Page title - desktop */}
              <div className="hidden lg:block min-w-0">
                <h1 className="text-lg font-bold text-forest-900 truncate leading-tight">
                  {pageTitle}
                </h1>
                <p className="text-xs text-gray-400 truncate leading-tight">{pageSubtitle}</p>
              </div>
            </div>

            {/* Right: search + role badge + theme + notifications + user */}
            <div className="flex items-center gap-2 sm:gap-3">
              {/* Global search trigger */}
              <button
                onClick={() => setPaletteOpen(true)}
                className="hidden md:flex items-center gap-2 px-3 py-1.5 rounded-lg border border-gray-300 text-gray-400 hover:border-primary-300 hover:text-gray-600 text-sm"
              >
                <Search className="w-4 h-4" />
                <span>Search…</span>
                <kbd className="text-[10px] border border-gray-200 rounded px-1 py-0.5 ml-2">Ctrl K</kbd>
              </button>
              <button
                onClick={() => setPaletteOpen(true)}
                className="md:hidden p-2 rounded-lg text-gray-400 hover:bg-gray-100 hover:text-gray-700"
                aria-label="Search"
              >
                <Search className="w-5 h-5" />
              </button>

              <span className="hidden sm:inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-primary-50 text-forest-900 text-xs font-semibold border border-primary-100">
                <ShieldCheck className="w-3.5 h-3.5 text-primary-500" />
                {DEV_AUTH_BYPASS ? 'Super Admin' : 'Admin'}
              </span>

              {/* Dark mode toggle */}
              <button
                onClick={() => setIsDark((v) => !v)}
                className="p-2 rounded-lg text-gray-500 hover:bg-gray-100 hover:text-gray-700"
                aria-label={isDark ? 'Switch to light mode' : 'Switch to dark mode'}
                title={isDark ? 'Light mode' : 'Dark mode'}
              >
                {isDark ? <Sun className="w-5 h-5" /> : <Moon className="w-5 h-5" />}
              </button>

              <NotificationBell />

              <div className="w-px h-7 bg-gray-200" />

              {/* User chip — Clerk UserButton when signed in, static fallback under dev bypass */}
              {DEV_AUTH_BYPASS ? (
                <div className="flex items-center gap-2.5 pl-0.5">
                  <div className="w-9 h-9 rounded-full bg-primary-400 text-white flex items-center justify-center font-bold text-sm shadow-sm">
                    SA
                  </div>
                  <div className="hidden md:block leading-tight">
                    <div className="text-sm font-semibold text-forest-900">Super Admin</div>
                    <div className="text-xs text-gray-400">dev@localhost</div>
                  </div>
                </div>
              ) : (
                <UserButton
                  afterSignOutUrl="/sign-in"
                  appearance={{ elements: { avatarBox: 'w-9 h-9' } }}
                />
              )}
            </div>
          </div>
        </header>

        {/* Page Content — explorer is a fixed-height, full-bleed shell (no page
            scroll; its columns scroll internally); other pages are padded & grow. */}
        <main className={isExplorer ? 'h-[calc(100vh-4rem)] overflow-hidden' : 'min-h-screen'}>
          <div className={isExplorer ? 'h-full' : 'p-6 lg:p-8'}>
            {children}
          </div>
        </main>
      </div>

      {/* Global search palette (Ctrl+K) */}
      <CommandPalette open={paletteOpen} onClose={() => setPaletteOpen(false)} />
    </div>
  );
}
